import { useTranslation } from 'react-i18next';
import { Pagination } from '@mantine/core';
import { useResizeObserver } from '@mantine/hooks';

import { GoogleFontIcon } from '../icons/GoogleFontIcon';
import { SingleSelect } from '../selects/SingleSelect';

import styles from './paginator.module.scss';

export type PaginatorProps = {
  activePage: number;
  activePaginationSize: number;
  availablePaginationSizes: number[];
  onPageChange: (newPage: number) => void;
  onPaginationSizeChange: (newPaginationSize: number) => void;
  totalEntries: number;
  selectedRowsCount?: number;
  boundaries?: number;
  entryLimit?: number;
  disabled?: boolean;
};

export function Paginator({
  activePage,
  activePaginationSize,
  availablePaginationSizes,
  onPageChange,
  onPaginationSizeChange,
  totalEntries,
  selectedRowsCount,
  boundaries,
  entryLimit,
  disabled,
}: PaginatorProps) {
  const { t } = useTranslation();
  const [ref, rect] = useResizeObserver();

  const totalPages = Math.ceil(totalEntries / activePaginationSize);
  const clampedTotalPages =
    entryLimit && totalEntries > entryLimit
      ? Math.floor(entryLimit / activePaginationSize)
      : totalPages;

  const isLastPageActive = activePage + 1 === clampedTotalPages;
  const isFirstPageActive = activePage === 0;

  // with all optional elements we need ~1010px of space to show everything
  const requiredWidthToShowCounts =
    1010 -
    // reserve space for page selector if shown
    (clampedTotalPages <= 1 ? 380 : 0) -
    // reserve space for selected row count if shown
    (selectedRowsCount == null || selectedRowsCount === 0 ? 120 : 0) -
    // reserve space for entry limit text if shown
    (!entryLimit || entryLimit >= totalEntries ? 160 : 0);
  const showLeftSectionCounts = rect.width > requiredWidthToShowCounts;

  return (
    <div className={styles['root']} data-testid="paginator" ref={ref}>
      <div className={styles['items-selected']} data-testid="rows-selected">
        {selectedRowsCount
          ? t('app.pagination.selectedRows', {
              selectedRowsCount,
            })
          : ''}
      </div>
      <div className={styles['left-section']}>
        <span
          style={{
            display: showLeftSectionCounts ? 'inline-block' : 'none',
          }}
        >
          {totalEntries < activePaginationSize
            ? t('app.pagination.summaryShort', { totalEntries })
            : t('app.pagination.summary', {
                firstShownEntry: activePage * activePaginationSize + 1,
                lastShownEntry: Math.min(
                  totalEntries,
                  (activePage + 1) * activePaginationSize
                ),
                totalEntries: totalEntries,
              })}
          {entryLimit &&
            totalEntries > entryLimit &&
            t('app.pagination.entryLimit', { totalEntries: entryLimit })}
        </span>
        {availablePaginationSizes.length > 1 && (
          <SingleSelect
            data={availablePaginationSizes.map((size) => {
              const sizeAsString = size.toString();
              return {
                label: t('app.pagination.selectItem', { entries: size }),
                value: sizeAsString,
              };
            })}
            disabled={disabled}
            clearable={false}
            onChange={(value) =>
              value && onPaginationSizeChange(Number.parseInt(value))
            }
            searchable={false}
            value={activePaginationSize.toString()}
          />
        )}
      </div>
      {clampedTotalPages > 1 && (
        <div className={styles['right-section']}>
          <Pagination
            siblings={rect.width > 720 ? 1 : 0}
            total={clampedTotalPages}
            value={activePage + 1}
            disabled={disabled}
            classNames={{
              dots: styles['dots'],
              control: styles['pagination__control'],
            }}
            boundaries={boundaries}
            previousIcon={() => (
              <GoogleFontIcon
                className={
                  isFirstPageActive || disabled
                    ? styles['disabled_navigation']
                    : styles['right-section__arrow-icon']
                }
                name="chevron_left"
              />
            )}
            nextIcon={() => (
              <GoogleFontIcon
                name="chevron_right"
                className={
                  isLastPageActive || disabled
                    ? styles['disabled_navigation']
                    : styles['right-section__arrow-icon']
                }
              />
            )}
            onChange={(value) => onPageChange(value - 1)}
            unstyled
          />
        </div>
      )}
    </div>
  );
}
